import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, Link } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Lenis from "lenis";
import { Navbar } from "./components/Navbar";
import { HomeTab } from "./components/HomeTab";
import { Timeline } from "./components/Timeline";
import { LiveAvailabilityFloatingButton } from "./components/LiveAvailabilityFloatingButton";
import { TimelineSkeleton, HobbySkeleton, CommunitySkeleton, ContactSkeleton, ProjectsSkeleton } from "./components/ui/Skeletons";
import { SimulatedLoading } from "./components/ui/SimulatedLoading";
import { professionalTimeline, academicTimeline } from "./lib/config";
import { DynamicContentProvider, useDynamicContent, getTimelineEvents } from "./lib/dynamicContent";
import "./lib/i18n";
import "./index.css";

// Lazy-loaded components for Page Tabs
const HobbyTab = lazy(() => import("./components/HobbyTab").then(m => ({ default: m.HobbyTab })));
const CommunityTab = lazy(() => import("./components/CommunityTab").then(m => ({ default: m.CommunityTab })));
const BlogTab = lazy(() => import("./components/BlogTab").then(m => ({ default: m.BlogTab })));
const ContactTab = lazy(() => import("./components/ContactTab").then(m => ({ default: m.ContactTab })));
const ProjectsTab = lazy(() => import("./components/ProjectsTab").then(m => ({ default: m.ProjectsTab })));
const FaqTab = lazy(() => import("./components/FaqTab").then(m => ({ default: m.FaqTab })));

// Lazy-loaded Toaster from sonner
const Toaster = lazy(() => import("sonner").then(m => ({ default: m.Toaster })));

function RootRedirect() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    const detected = i18n.language?.startsWith("tr") ? "tr" : "en";
    navigate(`/${detected}/home`, { replace: true });
  }, [navigate, i18n.language]);

  return null;
}

interface PageWrapperProps {
  tab: "home" | "professional" | "academic" | "community" | "hobby" | "blog" | "contact" | "projects" | "faq";
}

function getPageTitle(tab: string, isTr: boolean): string {
  const titles = {
    home: isTr
      ? "Ana Sayfa | Uğurcan Uzunkaya - AI & Yazılım Portfolyosu"
      : "Home | Uğurcan Uzunkaya - AI & Software Portfolio",
    professional: isTr
      ? "Deneyim | Uğurcan Uzunkaya - AI & Yazılım Mühendisi"
      : "Experience | Uğurcan Uzunkaya - AI & Software Engineer",
    academic: isTr
      ? "Akademik Geçmiş | Uğurcan Uzunkaya - AI & Yazılım Mühendisi"
      : "Academic History | Uğurcan Uzunkaya - AI & Software Engineer",
    community: isTr
      ? "Topluluk & Gönüllülük | Uğurcan Uzunkaya - AI & Yazılım"
      : "Community & Volunteering | Uğurcan Uzunkaya - AI & Software",
    hobby: isTr
      ? "Hobiler & İlgi Alanları | Uğurcan Uzunkaya - AI & Yazılım"
      : "Hobbies & Interests | Uğurcan Uzunkaya - AI & Software",
    projects: isTr
      ? "Projeler & Metrikler | Uğurcan Uzunkaya - AI & Yazılım"
      : "Projects & Showcase | Uğurcan Uzunkaya - AI & Software",
    blog: isTr
      ? "Blog & Makaleler | Uğurcan Uzunkaya - AI & Yazılım"
      : "Blog & Articles | Uğurcan Uzunkaya - AI & Software",
    contact: isTr
      ? "İletişim & Sosyal Medya | Uğurcan Uzunkaya - AI & Yazılım"
      : "Contact & Socials | Uğurcan Uzunkaya - AI & Software",
    faq: isTr
      ? "Sıkça Sorulan Sorular | Uğurcan Uzunkaya - AI & Yazılım"
      : "FAQ | Uğurcan Uzunkaya - AI & Software Portfolio Showcase"
  };
  return titles[tab as keyof typeof titles] || "";
}

function getPageDescription(tab: string, homeBio: string): string {
  const descriptions = {
    home: homeBio,
    professional: "Uğurcan Uzunkaya professional journey and milestones.",
    academic: "Academic history and engineering studies.",
    community: "Volunteer projects and core principles.",
    hobby: "Hobbies, reading lists, and interactive travel map.",
    projects: "Showcasing and detailing technical engineering projects and metrics.",
    blog: "Thoughts and writings on engineering, AI and leadership.",
    contact: "Social handles and contact form submissions.",
    faq: "Frequently Asked Questions about Uğurcan Uzunkaya's engineering and philosophies."
  };
  return descriptions[tab as keyof typeof descriptions] || "";
}

function PageFallback({ tab }: { tab: string }) {
  switch (tab) {
    case "hobby": return <HobbySkeleton />;
    case "community": return <CommunitySkeleton />;
    case "projects": return <ProjectsSkeleton />;
    case "contact": return <ContactSkeleton />;
    case "blog":
      return (
        <div className="max-w-7xl mx-auto py-6 space-y-8 animate-pulse text-left">
          <div className="text-center md:text-left space-y-2 border-b border-border/60 pb-5">
            <div className="h-8 w-48 bg-muted rounded mx-auto md:mx-0" />
            <div className="h-4 w-96 bg-muted rounded mx-auto md:mx-0 mt-2" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-border/60 bg-card/65 rounded-xl h-48 p-6 space-y-4" />
            ))}
          </div>
        </div>
      );
    case "faq":
      return (
        <div className="max-w-7xl mx-auto py-6 space-y-6 animate-fade-in text-left">
          <div className="h-10 w-48 bg-muted animate-pulse rounded-lg" />
          <div className="h-4 w-96 bg-muted animate-pulse rounded-lg mt-2" />
          <div className="space-y-4 pt-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 w-full bg-muted/65 animate-pulse rounded-2xl" />
            ))}
          </div>
        </div>
      );
    default: return <TimelineSkeleton />;
  }
}

function PageWrapper({ tab }: PageWrapperProps) {
  const { t, i18n } = useTranslation();
  const { lang = "en", slug } = useParams<{ lang: string; slug?: string }>();
  const { timeline, loading } = useDynamicContent();

  const canonicalUrl = `https://ugurcanuzunkaya.com/${lang}/${tab}${tab === "blog" && slug ? `/${slug}` : ""}`;

  useEffect(() => {
    if (lang && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  useEffect(() => {
    fetch("/api/analytics/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        pagePath: `/${tab}${slug ? `/${slug}` : ""}`,
        locale: lang
      })
    }).catch((err) => console.warn("Analytics tracking failed:", err));
  }, [tab, slug, lang]);

  const isTr = lang === "tr";


  const renderContent = () => {
    switch (tab) {
      case "home": return <HomeTab lang={lang as any} />;
      case "professional": {
        if (loading) {
          return <TimelineSkeleton />;
        }
        const events = getTimelineEvents("professional", timeline, professionalTimeline);
        return (
          <div className="max-w-7xl mx-auto py-6 animate-fade-in text-left">
            <h3 className="text-3xl font-extrabold text-brand-primary mb-6">{t("nav.professional")}</h3>
            <Timeline events={events} lang={lang as any} />
          </div>
        );
      }
      case "academic": {
        if (loading) {
          return <TimelineSkeleton />;
        }
        const events = getTimelineEvents("academic", timeline, academicTimeline);
        return (
          <div className="max-w-7xl mx-auto py-6 animate-fade-in text-left">
            <h3 className="text-3xl font-extrabold text-brand-primary mb-6">{t("nav.academic")}</h3>
            <Timeline events={events} lang={lang as any} />
          </div>
        );
      }
      case "community": return <CommunityTab lang={lang as any} />;
      case "hobby": return <HobbyTab lang={lang as any} />;
      case "projects": return <ProjectsTab lang={lang as any} />;
      case "blog": return <BlogTab />;
      case "contact": return <ContactTab lang={lang as any} />;
      case "faq": return <FaqTab lang={lang as any} />;
    }
  };

  const isHome = tab === "home";

  return (
    <>
      <Helmet>
        <title>{getPageTitle(tab, isTr)}</title>
        <meta name="description" content={getPageDescription(tab, t("home.bio"))} />
        <link rel="canonical" href={canonicalUrl} />
        <html lang={lang} />
      </Helmet>
      <Suspense fallback={<PageFallback tab={tab} />}>
        {isHome ? (
          renderContent()
        ) : (
          <SimulatedLoading fallback={<PageFallback tab={tab} />} delay={500}>
            {renderContent()}
          </SimulatedLoading>
        )}
      </Suspense>
    </>
  );
}

function Layout({ theme, toggleTheme }: { theme: "light" | "dark"; toggleTheme: () => void }) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  return (
    <div className="min-h-screen w-full flex flex-col bg-background text-foreground transition-colors duration-300">
      <div className="cyber-grid-overlay" />
      <div className="radial-spotlight" />
      <Navbar theme={theme} toggleTheme={toggleTheme} />

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 md:px-8 py-8 relative z-10">

        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/:lang/home" element={<PageWrapper tab="home" />} />
          <Route path="/:lang/professional" element={<PageWrapper tab="professional" />} />
          <Route path="/:lang/academic" element={<PageWrapper tab="academic" />} />
          <Route path="/:lang/community" element={<PageWrapper tab="community" />} />
          <Route path="/:lang/hobby" element={<PageWrapper tab="hobby" />} />
          <Route path="/:lang/projects" element={<PageWrapper tab="projects" />} />
          <Route path="/:lang/blog" element={<PageWrapper tab="blog" />} />
          <Route path="/:lang/blog/:slug" element={<PageWrapper tab="blog" />} />
          <Route path="/:lang/contact" element={<PageWrapper tab="contact" />} />
          <Route path="/:lang/faq" element={<PageWrapper tab="faq" />} />
          <Route path="*" element={<Navigate to={`/${currentLang}/home`} replace />} />
        </Routes>
      </main>

      <footer className="w-full border-t border-border/80 bg-card/40 py-6 px-4 md:px-8 text-center text-xs text-muted-foreground relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center justify-center gap-2">
            <p>© 2026 Uğurcan Uzunkaya. All rights reserved.</p>
            <span className="text-muted-foreground/30">•</span>
            <Link
              to={`/${currentLang}/faq`}
              className="hover:text-brand-primary transition-colors underline underline-offset-2"
            >
              {currentLang === "tr" ? "SSS" : "FAQ"}
            </Link>
          </div>
        </div>
      </footer>
      <LiveAvailabilityFloatingButton />
      <Suspense fallback={null}>
        <Toaster position="bottom-right" richColors />
      </Suspense>
    </div>
  );
}

export function App() {
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("theme");
      if (stored === "light" || stored === "dark") return stored;
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    let frameId: number;
    function raf(time: number) {
      lenis.raf(time);
      frameId = requestAnimationFrame(raf);
    }
    frameId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(frameId);
      lenis.destroy();
    };
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <HelmetProvider>
      <DynamicContentProvider>
        <BrowserRouter>
          <Layout theme={theme} toggleTheme={toggleTheme} />
        </BrowserRouter>
      </DynamicContentProvider>
    </HelmetProvider>
  );
}

export default App;
